import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * A widget a dashboard offers. The registry is an ordered list: the order inside a zone is the default order.
 * `removable: false` widgets can be moved but never hidden.
 */
export interface WidgetDef {
  id: string;
  title: string;
  /** The zone (row/area) the widget sits in by default. */
  zone: string;
  removable: boolean;
}

/** What is remembered per browser: the widget ids in each zone, the hidden widget ids and the lock flag. */
export interface DashboardLayoutState {
  zones: Record<string, string[]>;
  hidden: string[];
  locked: boolean;
}

/** Zone names in the order the registry first mentions them. */
export function zoneOrder(registry: readonly WidgetDef[]): string[] {
  const zones: string[] = [];
  for (const widget of registry) {
    if (!zones.includes(widget.zone)) zones.push(widget.zone);
  }
  return zones;
}

/** The layout a user gets before changing anything (and after "Reset to default layout"): unlocked, nothing hidden. */
export function defaultLayout(registry: readonly WidgetDef[]): DashboardLayoutState {
  const zones: Record<string, string[]> = {};
  for (const zone of zoneOrder(registry)) zones[zone] = [];
  for (const widget of registry) zones[widget.zone].push(widget.id);
  return { zones, hidden: [], locked: false };
}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

/**
 * Turns whatever was stored into a layout that is valid for the registry: unknown ids and unknown zones are dropped,
 * an id is listed once, a widget that cannot be removed is never hidden, and a widget the stored layout does not know
 * (added to the app later) appears in its default zone. Anything unusable gives the default layout.
 */
export function normaliseLayout(raw: unknown, registry: readonly WidgetDef[]): DashboardLayoutState {
  if (typeof raw !== 'object' || raw === null) return defaultLayout(registry);
  const stored = raw as Partial<Record<keyof DashboardLayoutState, unknown>>;
  const byId = new Map(registry.map((widget) => [widget.id, widget]));
  const seen = new Set<string>();
  const hidden: string[] = [];

  if (isStringArray(stored.hidden)) {
    for (const id of stored.hidden) {
      const widget = byId.get(id);
      if (widget?.removable && !seen.has(id)) {
        seen.add(id);
        hidden.push(id);
      }
    }
  }

  const zones: Record<string, string[]> = {};
  const storedZones =
    typeof stored.zones === 'object' && stored.zones !== null ? (stored.zones as Record<string, unknown>) : {};
  for (const zone of zoneOrder(registry)) {
    zones[zone] = [];
    const ids = storedZones[zone];
    if (!isStringArray(ids)) continue;
    for (const id of ids) {
      if (byId.has(id) && !seen.has(id)) {
        seen.add(id);
        zones[zone].push(id);
      }
    }
  }

  for (const widget of registry) {
    if (!seen.has(widget.id)) zones[widget.zone].push(widget.id);
  }

  return { zones, hidden, locked: stored.locked === true };
}

const sameLists = (a: DashboardLayoutState, b: DashboardLayoutState): boolean =>
  JSON.stringify([a.zones, a.hidden]) === JSON.stringify([b.zones, b.hidden]);

/** True when the widgets are where the registry puts them and none is hidden (the lock flag does not count). */
export function isDefaultArrangement(layout: DashboardLayoutState, registry: readonly WidgetDef[]): boolean {
  return sameLists(layout, defaultLayout(registry));
}

function locate(layout: DashboardLayoutState, id: string): { zone: string; index: number } | null {
  for (const [zone, ids] of Object.entries(layout.zones)) {
    const index = ids.indexOf(id);
    if (index >= 0) return { zone, index };
  }
  return null;
}

/**
 * Moves a visible widget to `index` of `zone`. `index` counts the zone as it is now (before the widget is lifted out),
 * so dropping "before the widget at 3" is index 3 whether the widget came from before or after it.
 */
export function moveWidget(
  layout: DashboardLayoutState,
  id: string,
  zone: string,
  index: number,
): DashboardLayoutState {
  if (layout.locked || !(zone in layout.zones)) return layout;
  const from = locate(layout, id);
  if (!from) return layout;
  const target = Math.max(0, Math.min(index, layout.zones[zone].length));
  const at = from.zone === zone && from.index < target ? target - 1 : target;
  if (from.zone === zone && from.index === at) return layout;
  const zones = { ...layout.zones };
  zones[from.zone] = zones[from.zone].filter((other) => other !== id);
  const list = [...zones[zone]];
  list.splice(at, 0, id);
  zones[zone] = list;
  return { ...layout, zones };
}

/**
 * Keyboard move: one place earlier (-1) or later (+1) in the order the widgets are shown, across zones (the first
 * widget of a zone moves up into the end of the zone above).
 */
export function shiftWidget(
  layout: DashboardLayoutState,
  registry: readonly WidgetDef[],
  id: string,
  delta: -1 | 1,
): DashboardLayoutState {
  if (layout.locked) return layout;
  const from = locate(layout, id);
  if (!from) return layout;
  const order = zoneOrder(registry);
  const ids = layout.zones[from.zone];
  const next = from.index + delta;
  if (next >= 0 && next < ids.length) {
    return moveWidget(layout, id, from.zone, delta === 1 ? next + 1 : next);
  }
  const zoneIndex = order.indexOf(from.zone) + delta;
  if (zoneIndex < 0 || zoneIndex >= order.length) return layout;
  const zone = order[zoneIndex];
  return moveWidget(layout, id, zone, delta === -1 ? layout.zones[zone].length : 0);
}

/** Hides a widget that can be removed. */
export function hideWidget(
  layout: DashboardLayoutState,
  registry: readonly WidgetDef[],
  id: string,
): DashboardLayoutState {
  const widget = registry.find((candidate) => candidate.id === id);
  if (layout.locked || !widget?.removable || layout.hidden.includes(id)) return layout;
  const from = locate(layout, id);
  if (!from) return layout;
  return {
    ...layout,
    zones: { ...layout.zones, [from.zone]: layout.zones[from.zone].filter((other) => other !== id) },
    hidden: [...layout.hidden, id],
  };
}

/** Puts a hidden widget back at the end of its default zone. */
export function showWidget(
  layout: DashboardLayoutState,
  registry: readonly WidgetDef[],
  id: string,
): DashboardLayoutState {
  const widget = registry.find((candidate) => candidate.id === id);
  if (layout.locked || !widget || !layout.hidden.includes(id)) return layout;
  return {
    ...layout,
    zones: { ...layout.zones, [widget.zone]: [...layout.zones[widget.zone], id] },
    hidden: layout.hidden.filter((other) => other !== id),
  };
}

function readStored(storageKey: string): unknown {
  try {
    const text = window.localStorage.getItem(storageKey);
    return text === null ? null : JSON.parse(text);
  } catch {
    return null;
  }
}

function writeStored(storageKey: string, layout: DashboardLayoutState, isDefault: boolean): void {
  try {
    if (isDefault && !layout.locked) {
      window.localStorage.removeItem(storageKey);
    } else {
      window.localStorage.setItem(storageKey, JSON.stringify(layout));
    }
  } catch {
    // Storage can be blocked or full: the layout then simply is not remembered.
  }
}

/**
 * Customisable dashboard layout (APT377, reused by the client dashboard): which widgets are shown, in which zone and
 * order, and whether the layout is locked. Remembered per browser under `storageKey`; unreadable or missing storage
 * falls back to the default layout. While locked, nothing can be moved, hidden or added.
 */
export function useDashboardLayout<W extends WidgetDef>(registry: readonly W[], storageKey: string) {
  const [layout, setLayout] = useState<DashboardLayoutState>(() => normaliseLayout(readStored(storageKey), registry));

  useEffect(() => {
    writeStored(storageKey, layout, isDefaultArrangement(layout, registry));
  }, [layout, storageKey, registry]);

  const byId = useMemo(() => new Map(registry.map((widget) => [widget.id, widget])), [registry]);

  const zoneWidgets = useCallback(
    (zone: string): W[] =>
      (layout.zones[zone] ?? []).map((id) => byId.get(id)).filter((widget): widget is W => widget !== undefined),
    [layout, byId],
  );

  const hiddenWidgets = useMemo(
    () => layout.hidden.map((id) => byId.get(id)).filter((widget): widget is W => widget !== undefined),
    [layout, byId],
  );

  const isShown = useCallback((id: string) => !layout.hidden.includes(id), [layout]);

  /** Whether the widget has an earlier / later place to move to. */
  const canShift = useCallback(
    (id: string, delta: -1 | 1): boolean => !layout.locked && shiftWidget(layout, registry, id, delta) !== layout,
    [layout, registry],
  );

  return {
    layout,
    locked: layout.locked,
    zones: zoneOrder(registry),
    zoneWidgets,
    hiddenWidgets,
    isShown,
    isDefault: isDefaultArrangement(layout, registry),
    canShift,
    setLocked: (locked: boolean) => setLayout((current) => ({ ...current, locked })),
    move: (id: string, zone: string, index: number) => setLayout((current) => moveWidget(current, id, zone, index)),
    shift: (id: string, delta: -1 | 1) => setLayout((current) => shiftWidget(current, registry, id, delta)),
    hide: (id: string) => setLayout((current) => hideWidget(current, registry, id)),
    show: (id: string) => setLayout((current) => showWidget(current, registry, id)),
    /** Back to the registry's arrangement with nothing hidden; the lock flag is kept (reset is blocked while locked). */
    reset: () =>
      setLayout((current) => (current.locked ? current : { ...defaultLayout(registry), locked: current.locked })),
  };
}

export type DashboardLayoutApi<W extends WidgetDef = WidgetDef> = ReturnType<typeof useDashboardLayout<W>>;
